import { useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const COLOR = { ink: "var(--pr-ink)", ink2: "var(--pr-ink2)", muted: "var(--pr-muted)", gold: "var(--pr-gold)", line: "var(--pr-line)" };

const REASONS = [
  "Money's tight right now",
  "It didn't fit my rhythm",
  "Too much to keep up with",
  "I got what I needed",
  "Something else",
];

/** One gentle question after a cancellation. Never a retention wall. */
export const CancelSurveyCard = ({ onDone }) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (payload) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/caddy/cancel-survey`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("failed");
      if (!payload.skipped) toast.success("Thank you. That genuinely helps 🏁");
      onDone?.();
    } catch {
      toast.error("Couldn't save that, try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl p-5 sm:p-6"
      style={{ background: "var(--pr-card)", border: `1px solid ${COLOR.line}`, borderLeft: `5px solid ${COLOR.gold}` }}
      data-testid="cancel-survey-card"
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] mb-1.5" style={{ color: "var(--pr-accent-gold-text)" }}>
        One question, then I'll leave you be
      </p>
      <h3 className="text-lg mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
        What got in the way?
      </h3>
      <p className="text-sm leading-relaxed mb-4" style={{ color: COLOR.ink2 }}>
        You cancelled, and that's completely allowed. Your sheet and every number on it stay yours.
        If you're up for it, tell me what happened so the next racer has an easier time.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {REASONS.map((r) => (
          <button
            key={r}
            onClick={() => setReason(r)}
            className="px-3.5 py-2 rounded-full text-sm font-semibold border transition-colors"
            style={{
              background: reason === r ? "var(--pr-ink-surface)" : "var(--pr-card)",
              color: reason === r ? "#FFFFFF" : COLOR.ink2,
              borderColor: reason === r ? COLOR.ink : COLOR.line,
            }}
            data-testid={`cancel-reason-${REASONS.indexOf(r)}`}
          >
            {r}
          </button>
        ))}
      </div>

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 400))}
        rows={2}
        placeholder="Anything else? (optional)"
        className="w-full rounded-xl border p-3 text-sm mb-4 focus:outline-none"
        style={{ borderColor: COLOR.line, color: COLOR.ink }}
        data-testid="cancel-survey-note"
      />

      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => send({ reason, note })}
          disabled={busy || (!reason && !note.trim())}
          className="px-6 py-2.5 rounded-full font-bold text-sm transition-transform hover:scale-105 disabled:opacity-40"
          style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)" }}
          data-testid="cancel-survey-submit"
        >
          {busy ? "Sending…" : "Send it"}
        </button>
        <button
          onClick={() => send({ skipped: true })}
          disabled={busy}
          className="text-sm underline"
          style={{ color: COLOR.muted }}
          data-testid="cancel-survey-skip"
        >
          No thanks
        </button>
      </div>
    </div>
  );
};

export default CancelSurveyCard;
